import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ArrowLeft, Save, Info, AlertTriangle, CheckCircle2, Store, CalendarDays, Lock } from 'lucide-react';
import { LOJAS_AUDITAVEIS } from '../../../utils/empresas';

// =====================================================================
// Autoavaliação de Controles — o PREENCHIMENTO (o gerente responde).
//
// ⚠️ O GERENTE NÃO VÊ SELETOR DE LOJA, e é a regra central do formulário: a loja
//    dele vem do token, decidida no backend. É uma AUTOAVALIAÇÃO — ele responde
//    pela própria loja. Um seletor deixaria responder pela loja do vizinho e,
//    pior, consumir o único registro do mês daquela loja.
//    (No BSV existe seletor porque lá quem preenche é o escritório, visitando.)
//    A ÚNICA exceção é quem NÃO TEM loja no cadastro — escritório e Admin: para
//    esses o seletor aparece, senão o Admin não conseguiria testar a tela. Quem
//    decide continua sendo o backend: com loja no token, o que a tela manda é
//    ignorado.
//
// ⚠️ Cada "Não" abre 4 campos OBRIGATÓRIOS (motivo, plano de ação, responsável,
//    prazo). O botão de finalizar fica travado enquanto faltar qualquer um —
//    mas quem de fato valida é o backend; isto é a cortesia de não deixar o
//    gerente descobrir no envio.
//
// ⚠️ N/A só aparece nas perguntas que o aceitam (6 das 22, marcadas por
//    PERMITE_NA no banco). Não é enfeite: N/A sai do denominador do risco, e
//    liberá-lo em todas deixaria a loja encolher a própria régua.
//
// ⚠️⚠️ AS SEÇÕES ABREM UMA A UMA (2026-09-04). A seção seguinte só destrava
//    quando TODAS as perguntas da anterior estiverem CONCLUÍDAS — e "concluída"
//    inclui os 4 campos do plano quando a resposta foi "Não". É o que impede
//    varrer as 22 perguntas e deixar os planos de ação pela metade para o fim,
//    que era o caminho fácil quando tudo abria de uma vez.
//    ⚠️ O desbloqueio é MONOTÔNICO (marca d'água em `maxLiberada`): seção que
//    já abriu NUNCA fecha de novo. Sem isso, voltar numa pergunta anterior e
//    trocá-la para "Não" esconderia atrás de um cadeado o trabalho já feito
//    adiante — e a tela piscaria travada a cada tecla enquanto o plano de ação
//    daquela pergunta é digitado. O que a marca d'água NÃO afrouxa é o envio:
//    `podeEnviar` continua exigindo tudo completo.
//    ⚠️ ISTO É CORTESIA DE TELA, NÃO CONTROLE. O backend não sabe (nem precisa
//    saber) em que ordem as respostas foram digitadas — ele recebe as 22 de uma
//    vez e continua sendo quem recusa formulário incompleto.
// =====================================================================

const API_BASE = '/api/operacional/autoavaliacao';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const COR = '#475569'; // ardósia do módulo Operacional

// ⚠️ RÉPLICA das faixas do backend (server/operacional/autoavaliacao/autoavaliacao-score.js),
// para o gerente ver o risco subir enquanto responde. O backend RECALCULA e é a
// fonte da verdade — mudou lá, muda aqui.
//
// As cores aqui são SEMÂNTICAS (o que o número é), não a identidade do módulo —
// a exceção registrada em docs/ARQUITETURA.md › Paleta dos módulos. A ardósia
// segue mandando nos títulos, botões e bordas da tela.
const classificarRisco = (fracao) => {
  if (fracao <= 0.10) return { nome: 'Baixo', cor: '#059669', bg: '#d1fae5', icone: '🟢' };
  if (fracao <= 0.25) return { nome: 'Médio', cor: '#b45309', bg: '#fef3c7', icone: '🟡' };
  if (fracao <= 0.45) return { nome: 'Crítico', cor: '#c2410c', bg: '#ffedd5', icone: '🟠' };
  return { nome: 'Alto', cor: '#b91c1c', bg: '#fee2e2', icone: '🔴' };
};
const RISCO_NA = { nome: 'N/A', cor: '#475569', bg: '#f1f5f9', icone: '⚪' };

// Os 4 campos do "Não" — a ordem é a da tela.
const CAMPOS_PLANO = [
  { chave: 'motivo', rotulo: 'Motivo', dica: 'Qual é o problema ou a não conformidade?', tipo: 'texto', linhas: 2 },
  { chave: 'plano_acao', rotulo: 'Plano de ação', dica: 'O que será feito para corrigir?', tipo: 'texto', linhas: 2 },
  { chave: 'responsavel', rotulo: 'Responsável', dica: 'Quem responde pela correção?', tipo: 'linha' },
  { chave: 'prazo', rotulo: 'Prazo', dica: 'Data prevista para concluir', tipo: 'data' },
];

// Uma pergunta está CONCLUÍDA quando dá para seguir em frente: respondida e —
// se a resposta foi "Não" — com os 4 campos do plano preenchidos. É esta função
// que decide o cadeado da próxima seção; responder "Não" e deixar o plano em
// branco NÃO conclui a pergunta.
const perguntaConcluida = (resp) => {
  if (!resp) return false;
  if (resp.naoAplicavel) return true;
  if (resp.atende === 1) return true;
  if (resp.atende !== 0) return false; // sem resposta
  return CAMPOS_PLANO.every((c) => String(resp[c.chave] ?? '').trim());
};

// 'YYYY-MM' → 'setembro de 2026' (o gerente lê mês, não código).
const mesPorExtenso = (anomes) => {
  if (!anomes) return '';
  const [ano, mes] = anomes.split('-');
  const nomes = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  return `${nomes[Number(mes) - 1] || mes} de ${ano}`;
};

const AutoavaliacaoQuestionario = ({ onVoltar, onGravou }) => {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [observacaoGeral, setObservacaoGeral] = useState('');
  // respostas[idPergunta] = { atende: 1|0, naoAplicavel, motivo, plano_acao, responsavel, prazo }
  const [respostas, setRespostas] = useState({});
  // Só destaca o que falta DEPOIS da primeira tentativa de enviar — pintar de
  // vermelho um formulário que a pessoa ainda não começou é hostil.
  const [tentouEnviar, setTentouEnviar] = useState(false);
  // ⚠️ SÓ PARA ESCRITÓRIO/ADMIN, que não têm loja no cadastro: sem isto o Admin
  // não conseguiria testar o formulário pela tela (o backend recusaria com "seu
  // cadastro não tem loja"). Para o GERENTE este campo nem existe — a loja dele
  // vem do token, e é isso que impede responder pela loja do vizinho.
  const [lojaEscolhida, setLojaEscolhida] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const r = await fetch(`${API_BASE}/formulario`, { headers: auth() });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Falha ao carregar o formulário.');
      setDados(d);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // A loja do token manda; a escolhida só entra quando não há loja no cadastro.
  const lojaEfetiva = dados?.loja || lojaEscolhida || '';

  const areas = useMemo(() => dados?.areas || [], [dados]);

  const perguntas = useMemo(
    () => areas.flatMap((a) => a.perguntas.map((p) => ({ ...p, area: a.NOME }))),
    [areas]
  );

  const responder = (id, atende) =>
    setRespostas((r) => ({ ...r, [id]: { ...r[id], atende, naoAplicavel: false } }));

  // Liga/desliga o N/A. ⚠️ Ao ligar, LIMPA os 4 campos do plano: plano de ação
  // para pergunta que não se aplica é lixo que alguém vai cobrar depois. (O
  // backend faz a mesma limpeza — isto é o espelho na tela.)
  const alternarNa = (id) =>
    setRespostas((r) => {
      const atual = r[id] || {};
      const liga = !atual.naoAplicavel;
      return {
        ...r,
        [id]: liga
          ? { naoAplicavel: true, atende: undefined }
          : { ...atual, naoAplicavel: false },
      };
    });

  const setCampo = (id, chave, valor) =>
    setRespostas((r) => ({ ...r, [id]: { ...r[id], [chave]: valor } }));

  const ehNao = (resp) => resp && !resp.naoAplicavel && resp.atende === 0;

  // Risco ao vivo — contagem, igual ao backend.
  const resumo = useMemo(() => {
    let qtdNao = 0;
    let qtdNa = 0;
    for (const p of perguntas) {
      const resp = respostas[p.ID];
      if (resp?.naoAplicavel) qtdNa++;
      else if (resp?.atende === 0) qtdNao++;
    }
    const qtdAplicavel = perguntas.length - qtdNa;
    const fracao = qtdAplicavel > 0 ? qtdNao / qtdAplicavel : 0;
    return {
      qtdNao, qtdNa, qtdAplicavel,
      pct: fracao * 100,
      classe: qtdAplicavel > 0 ? classificarRisco(fracao) : RISCO_NA,
    };
  }, [perguntas, respostas]);

  const respondidas = useMemo(
    () => perguntas.filter((p) => {
      const r = respostas[p.ID];
      return r && (r.naoAplicavel || r.atende === 0 || r.atende === 1);
    }).length,
    [perguntas, respostas]
  );

  // Perguntas "Não" com algum dos 4 campos em branco — é o que trava o envio.
  const planosIncompletos = useMemo(
    () => perguntas.filter((p) => {
      const r = respostas[p.ID];
      if (!ehNao(r)) return false;
      return CAMPOS_PLANO.some((c) => !String(r[c.chave] ?? '').trim());
    }),
    [perguntas, respostas]
  );

  // ---- Desbloqueio progressivo (seção a seção) --------------------------
  // Progresso de cada seção: é ele que decide o cadeado da seguinte.
  const statusAreas = useMemo(
    () => areas.map((a) => {
      const concluidas = a.perguntas.filter((p) => perguntaConcluida(respostas[p.ID])).length;
      // Seção sem perguntas conta como concluída de propósito: se uma versão
      // futura vier com uma seção vazia, ela seria um muro intransponível.
      return { total: a.perguntas.length, concluidas, completa: concluidas === a.perguntas.length };
    }),
    [areas, respostas]
  );

  // A fronteira é a PRIMEIRA seção incompleta — a que está liberada agora.
  // Tudo concluído: a fronteira fica na última (não há próxima para abrir).
  const fronteira = useMemo(() => {
    const i = statusAreas.findIndex((s) => !s.completa);
    return i === -1 ? Math.max(0, statusAreas.length - 1) : i;
  }, [statusAreas]);

  // Marca d'água: a seção mais adiantada já aberta. Só sobe — ver o cabeçalho
  // do arquivo (é o que evita o cadeado voltar em cima de trabalho já feito).
  const [maxLiberada, setMaxLiberada] = useState(0);
  useEffect(() => { setMaxLiberada((m) => Math.max(m, fronteira)); }, [fronteira]);

  // Ao destravar uma seção nova, levar o gerente até ela: senão ela nasce fora
  // da tela, abaixo da seção que ele acabou de terminar, e parece que nada
  // aconteceu ao responder a última pergunta.
  const refsAreas = useRef([]);
  const ultimaRolada = useRef(0);
  useEffect(() => {
    if (maxLiberada > ultimaRolada.current) {
      ultimaRolada.current = maxLiberada;
      refsAreas.current[maxLiberada]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [maxLiberada]);

  // Observações gerais e envio só abrem com o questionário inteiro concluído.
  const tudoConcluido = statusAreas.length > 0 && statusAreas.every((s) => s.completa);
  // -----------------------------------------------------------------------

  const faltamResponder = perguntas.length - respondidas;
  const podeEnviar = perguntas.length > 0 && faltamResponder === 0
    && planosIncompletos.length === 0 && Boolean(lojaEfetiva);

  const enviar = async () => {
    setTentouEnviar(true);
    if (!lojaEfetiva) {
      setErro('Escolha a loja da autoavaliação.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (faltamResponder > 0) {
      setErro(`Responda todas as ${perguntas.length} perguntas. Faltam ${faltamResponder}.`);
      return;
    }
    if (planosIncompletos.length > 0) {
      setErro(
        `Complete o plano de ação das ${planosIncompletos.length} pergunta(s) marcadas "Não" `
        + '— motivo, plano de ação, responsável e prazo são obrigatórios.'
      );
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      const r = await fetch(`${API_BASE}/submit`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_versao: dados.versao.ID,
          observacao_geral: observacaoGeral || null,
          // O backend IGNORA isto quando o token tem loja — só serve ao
          // escritório/Admin, que não tem uma.
          loja: lojaEfetiva || null,
          respostas: perguntas.map((p) => {
            const resp = respostas[p.ID] || {};
            const na = !!resp.naoAplicavel;
            const marcouNao = !na && resp.atende === 0;
            return {
              id_pergunta: p.ID,
              atende: na ? 1 : resp.atende,
              nao_aplicavel: na ? 1 : 0,
              motivo: marcouNao ? resp.motivo : null,
              plano_acao: marcouNao ? resp.plano_acao : null,
              responsavel: marcouNao ? resp.responsavel : null,
              prazo: marcouNao ? resp.prazo : null,
            };
          }),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Falha ao gravar.');
      onGravou?.(d);
    } catch (e) {
      setErro(e.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSalvando(false);
    }
  };

  if (loading) return <p style={{ color: '#64748b' }}>Carregando o formulário…</p>;
  if (erro && !dados) {
    return (
      <div>
        <BotaoVoltar onVoltar={onVoltar} />
        <p style={{ color: '#dc2626' }}>{erro}</p>
      </div>
    );
  }

  // Já respondeu o mês: mostra o aviso em vez do formulário. (A trava de
  // verdade é o 409 do backend; isto evita o gerente preencher 22 perguntas
  // para descobrir no fim.)
  if (dados?.ja_respondido) {
    return (
      <div>
        <BotaoVoltar onVoltar={onVoltar} />
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderLeft: `4px solid ${COR}`,
          borderRadius: 12, padding: 24,
        }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
            <CheckCircle2 size={18} style={{ verticalAlign: -3, marginRight: 6, color: '#059669' }} />
            A autoverificação de {mesPorExtenso(dados.anomes)} já foi respondida
          </h3>
          <p style={{ margin: '0 0 4px', color: '#475569', fontSize: 14 }}>
            Loja <strong>{dados.loja}</strong> · respondida em{' '}
            {String(dados.ja_respondido.DATA_PREENCHIMENTO).slice(0, 10).split('-').reverse().join('/')} ·
            risco <strong>{Number(dados.ja_respondido.PERCENTUAL_RISCO).toFixed(2)}%</strong>
          </p>
          <p style={{ margin: '12px 0 0', color: '#64748b', fontSize: 13 }}>
            É uma por loja por mês. Para refazer, abra a autoverificação na consulta e
            <strong> cancele informando o motivo</strong> — a resposta anterior fica registrada.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{'.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      <BotaoVoltar onVoltar={onVoltar} />

      {/* Cabeçalho: a loja e o mês são INFORMADOS, não escolhidos. */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
        padding: 20, marginBottom: 16,
      }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
          Autoavaliação de Controles Financeiros e Operacionais
        </h3>
        <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 13 }}>
          {dados.versao?.NOME_VERSAO} · {perguntas.length} perguntas
        </p>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {dados.loja ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#334155' }}>
              <Store size={16} color={COR} /> Loja <strong>{dados.loja}</strong>
            </span>
          ) : (
            // Só cai aqui quem não tem loja no cadastro (escritório/Admin).
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#334155' }}>
              <Store size={16} color={COR} />
              <label htmlFor="loja-autoavaliacao">Loja</label>
              <select
                id="loja-autoavaliacao"
                value={lojaEscolhida}
                onChange={(e) => setLojaEscolhida(e.target.value)}
                style={{
                  padding: '6px 8px', border: `1px solid ${tentouEnviar && !lojaEfetiva ? '#f87171' : '#cbd5e1'}`,
                  borderRadius: 6, fontFamily: 'inherit', fontSize: 13, background: '#fff',
                }}
              >
                <option value="">— escolher —</option>
                {LOJAS_AUDITAVEIS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <span style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600 }}>
                seu cadastro não tem loja — o gerente não vê esta escolha
              </span>
            </span>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#334155' }}>
            <CalendarDays size={16} color={COR} /> Referência <strong>{mesPorExtenso(dados.anomes)}</strong>
          </span>
        </div>
      </div>

      {erro && (
        <div style={{
          padding: 12, background: '#fee2e2', color: '#991b1b',
          borderRadius: 8, marginBottom: 12, fontSize: 13, fontWeight: 600,
        }}>
          {erro}
        </div>
      )}

      {/* Risco ao vivo + progresso (acompanha a rolagem) */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 5,
        background: resumo.classe.bg, border: `2px solid ${resumo.classe.cor}`,
        borderRadius: 12, padding: 16, marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 26 }}>{resumo.classe.icone}</div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', letterSpacing: 0.5 }}>
              RISCO APURADO
            </div>
            <div style={{
              fontSize: 21, fontWeight: 800, color: resumo.classe.cor,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {resumo.qtdNao} de {resumo.qtdAplicavel} · {resumo.pct.toFixed(2)}% · {resumo.classe.nome}
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
            {respondidas} / {perguntas.length} respondidas
            {resumo.qtdNa > 0 && <> · {resumo.qtdNa} N/A fora do cálculo</>}
            {/* A faixa acompanha a rolagem: é o único lugar onde "em que seção
                eu estou" fica visível o tempo todo. */}
            {!tudoConcluido && areas.length > 0 && (
              <div style={{ fontWeight: 800, color: COR, marginTop: 2 }}>
                Seção {fronteira + 1} de {areas.length}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginTop: 10, height: 8, background: 'rgba(255,255,255,.6)', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 999, transition: 'width .2s',
            width: `${perguntas.length ? (respondidas / perguntas.length) * 100 : 0}%`,
            background: resumo.classe.cor,
          }} />
        </div>
      </div>

      {/* Seções — abrem uma a uma (ver o cabeçalho do arquivo) */}
      {areas.map((area, idxArea) => {
        // Numeração contínua das perguntas (1..22), como no documento.
        const antes = areas.slice(0, idxArea)
          .reduce((acc, a) => acc + a.perguntas.length, 0);
        const st = statusAreas[idxArea] || { total: area.perguntas.length, concluidas: 0, completa: false };
        const bloqueada = idxArea > maxLiberada;
        return (
          <div
            key={area.ID}
            ref={(el) => { refsAreas.current[idxArea] = el; }}
            style={{
              background: bloqueada ? '#f8fafc' : '#fff',
              border: `1px ${bloqueada ? 'dashed' : 'solid'} #e2e8f0`,
              borderRadius: 12, padding: 20, marginBottom: 16,
              // A faixa de risco é sticky no topo: sem esta margem, a seção
              // recém-aberta pararia embaixo dela ao rolar.
              scrollMarginTop: 130,
            }}
          >
            <h4 style={{
              margin: '0 0 14px', fontSize: 15, fontWeight: 800,
              color: bloqueada ? '#94a3b8' : '#0f172a',
              paddingBottom: 10, borderBottom: '1px solid #f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap',
            }}>
              <span>
                <span style={{ color: bloqueada ? '#94a3b8' : COR }}>{idxArea + 1}.</span> {area.NOME}
              </span>
              <SeloSecao bloqueada={bloqueada} secaoAnterior={idxArea} st={st} />
            </h4>

            <div
              style={{
                display: 'grid', gap: 12,
                // A seção travada continua LEGÍVEL (o gerente vê o tamanho do
                // que vem pela frente), só não é respondível.
                ...(bloqueada ? { opacity: 0.45, pointerEvents: 'none', userSelect: 'none' } : null),
              }}
            >
              {area.perguntas.map((p, idxP) => {
                const resp = respostas[p.ID] || {};
                const marcouNao = ehNao(resp);
                const semResposta = !(resp.naoAplicavel || resp.atende === 0 || resp.atende === 1);
                // Não se pinta de vermelho o que a própria tela não deixou responder.
                const destacar = tentouEnviar && semResposta && !bloqueada;
                return (
                  <div key={p.ID} style={{
                    padding: 14, borderRadius: 10,
                    background: marcouNao ? '#fef2f2' : '#f8fafc',
                    border: destacar ? '1px solid #f87171' : '1px solid transparent',
                  }}>
                    <div style={{
                      display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto',
                      gap: 12, alignItems: 'start',
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: '#0f172a', fontSize: 14, lineHeight: 1.5 }}>
                          <strong style={{ color: COR, fontVariantNumeric: 'tabular-nums' }}>
                            {antes + idxP + 1}.
                          </strong>{' '}
                          {p.TEXTO}
                        </div>

                        {/* A orientação "Verificar: ..." do documento — instrução, não
                            pergunta. `pre-line` preserva os sub-itens da pergunta 6. */}
                        {p.ORIENTACAO && (
                          <div style={{
                            marginTop: 8, padding: '8px 10px', background: '#fff',
                            border: '1px dashed #cbd5e1', borderRadius: 8,
                            color: '#64748b', fontSize: 12.5, lineHeight: 1.6,
                            whiteSpace: 'pre-line',
                          }}>
                            {p.ORIENTACAO}
                          </div>
                        )}

                        {resp.naoAplicavel && (
                          <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: '#64748b' }}>
                            ⚪ Não aplicável — fora do cálculo de risco.
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {/* `disabled` além do pointerEvents do bloco: o cadeado
                            também tem de valer para o teclado. */}
                        <button type="button" disabled={bloqueada} onClick={() => responder(p.ID, 1)}
                          style={botaoResposta(resp.atende === 1 && !resp.naoAplicavel, '#059669')}>
                          Sim
                        </button>
                        <button type="button" disabled={bloqueada} onClick={() => responder(p.ID, 0)}
                          style={botaoResposta(resp.atende === 0 && !resp.naoAplicavel, '#dc2626')}>
                          Não
                        </button>
                        {/* ⚠️ Só nas perguntas que aceitam N/A (PERMITE_NA). */}
                        {p.PERMITE_NA ? (
                          <button type="button" disabled={bloqueada} onClick={() => alternarNa(p.ID)}
                            style={botaoResposta(resp.naoAplicavel === true, '#64748b')}>
                            N/A
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {/* Os 4 campos obrigatórios do "Não". */}
                    {marcouNao && (
                      <div style={{
                        marginTop: 14, paddingTop: 14, borderTop: '1px dashed #fca5a5',
                        display: 'grid', gap: 10,
                      }}>
                        <div style={{
                          fontSize: 12, fontWeight: 800, color: '#b91c1c',
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}>
                          <AlertTriangle size={14} /> Não conformidade — os 4 campos são obrigatórios
                        </div>
                        {/* Onde a regra do desbloqueio realmente morde: o "Não"
                            sem plano não conclui a pergunta, e a seção seguinte
                            não abre. Dizer isso aqui evita o gerente procurar
                            por que a próxima seção continua no cadeado. */}
                        <div style={{ fontSize: 11.5, color: '#7f1d1d', marginTop: -4 }}>
                          Enquanto eles estiverem em branco, esta pergunta não conta como concluída
                          e a próxima seção continua bloqueada.
                        </div>
                        <div style={{
                          display: 'grid', gap: 10,
                          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                        }}>
                          {CAMPOS_PLANO.map((c) => {
                            const valor = resp[c.chave] || '';
                            const vazio = !String(valor).trim();
                            const borda = tentouEnviar && vazio ? '#f87171' : '#cbd5e1';
                            const comum = {
                              width: '100%', boxSizing: 'border-box', padding: 8,
                              border: `1px solid ${borda}`, borderRadius: 6,
                              fontFamily: 'inherit', fontSize: 13, background: '#fff',
                            };
                            return (
                              <div key={c.chave} style={c.tipo === 'texto' ? { gridColumn: '1 / -1' } : undefined}>
                                <label style={{ fontSize: 11, fontWeight: 800, color: '#7f1d1d' }}>
                                  {c.rotulo} *
                                </label>
                                {c.tipo === 'texto' ? (
                                  <textarea
                                    rows={c.linhas} value={valor} placeholder={c.dica}
                                    onChange={(e) => setCampo(p.ID, c.chave, e.target.value)}
                                    style={{ ...comum, marginTop: 4, resize: 'vertical' }}
                                  />
                                ) : (
                                  <input
                                    type={c.tipo === 'data' ? 'date' : 'text'}
                                    value={valor}
                                    placeholder={c.tipo === 'data' ? undefined : c.dica}
                                    onChange={(e) => setCampo(p.ID, c.chave, e.target.value)}
                                    style={{ ...comum, marginTop: 4 }}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Observações gerais (opcional) */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
        padding: 20, marginBottom: 16,
      }}>
        <h4 style={{
          margin: '0 0 12px', fontSize: 15, fontWeight: 800,
          color: tudoConcluido ? COR : '#94a3b8',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          Observações gerais
          {!tudoConcluido && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 11.5, fontWeight: 700, color: '#64748b',
              background: '#f1f5f9', border: '1px solid #e2e8f0',
              borderRadius: 999, padding: '3px 10px',
            }}>
              <Lock size={12} /> Conclua as {areas.length} seções para liberar
            </span>
          )}
        </h4>
        {/* A observação geral é o fecho do formulário: abre quando o
            questionário inteiro está concluído, no mesmo desenho das seções. */}
        <textarea
          rows={4} value={observacaoGeral} disabled={!tudoConcluido}
          onChange={(e) => setObservacaoGeral(e.target.value)}
          placeholder={tudoConcluido
            ? 'Algo que o plano de ação de uma pergunta específica não cobre (opcional).'
            : 'Disponível ao concluir todas as seções.'}
          style={{
            width: '100%', boxSizing: 'border-box', padding: 10,
            border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit',
            fontSize: 13, resize: 'vertical',
            background: tudoConcluido ? '#fff' : '#f8fafc',
            color: tudoConcluido ? 'inherit' : '#94a3b8',
            cursor: tudoConcluido ? 'auto' : 'not-allowed',
          }}
        />
        <div style={{
          marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 10,
          background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10,
          padding: '12px 14px', color: '#334155', fontSize: 13, lineHeight: 1.6,
        }}>
          <Info size={18} style={{ flexShrink: 0, marginTop: 1, color: COR }} />
          <span>
            As seções são respondidas <strong>na ordem</strong>: a seguinte abre quando todas as
            perguntas da anterior estiverem concluídas — e uma pergunta marcada <strong>“Não” só
            conclui com os 4 campos do plano preenchidos</strong>. Seção já aberta continua
            editável.
            <br /><br />
            O risco é <strong>o nº de “Não” dividido pelas perguntas aplicáveis</strong>. Pergunta
            marcada <strong>N/A sai da conta</strong> — não conta a favor nem contra.
            É <strong>uma autoavaliação por mês</strong>, e ao finalizar ela não pode ser editada:
            correção é cancelar informando o motivo e responder de novo.
          </span>
        </div>
      </div>

      {/* Envio */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', paddingBottom: 8 }}>
        <button
          type="button" onClick={enviar} disabled={salvando}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 24px', borderRadius: 8, border: 'none',
            background: salvando ? '#94a3b8' : (podeEnviar ? '#059669' : COR),
            color: '#fff', fontWeight: 800, fontSize: 15,
            cursor: salvando ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}
        >
          <Save size={18} /> {salvando ? 'Gravando…' : 'Finalizar autoavaliação'}
        </button>
        {!podeEnviar && (
          <span style={{ fontSize: 13, color: '#b45309', fontWeight: 600 }}>
            {/* Com as seções em ordem, dizer "faltam 14 perguntas" é inútil: o
                gerente só pode agir na seção aberta. Aponta-se para ela. */}
            {!tudoConcluido && areas[fronteira] && (
              <>Seção {fronteira + 1} de {areas.length} — <em>{areas[fronteira].NOME}</em>:{' '}
                {statusAreas[fronteira].concluidas}/{statusAreas[fronteira].total} concluídas.
              </>
            )}
            {/* Concluído tudo, o único bloqueio que sobra é a loja — e ela só
                falta para escritório/Admin, que escolhem no cabeçalho. */}
            {tudoConcluido && !lojaEfetiva && 'Escolha a loja da autoavaliação, no topo da tela.'}
          </span>
        )}
      </div>
    </div>
  );
};

const BotaoVoltar = ({ onVoltar }) => (
  <div style={{ marginBottom: 16 }}>
    <button
      type="button" onClick={onVoltar}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        color: '#475569', fontFamily: 'inherit', fontSize: 13,
      }}
    >
      <ArrowLeft size={16} /> Voltar
    </button>
  </div>
);

// O selo à direita do título da seção — diz em que pé ela está: travada
// (com a seção que precisa terminar antes), em andamento, ou concluída.
// ⚠️ "Concluídas", não "respondidas": um "Não" sem plano de ação está
// respondido e mesmo assim não conta.
const SeloSecao = ({ bloqueada, secaoAnterior, st }) => {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 11.5, fontWeight: 700, borderRadius: 999,
    padding: '3px 10px', fontVariantNumeric: 'tabular-nums',
  };
  if (bloqueada) {
    return (
      <span style={{ ...base, color: '#64748b', background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
        <Lock size={12} /> Conclua a seção {secaoAnterior} para liberar
      </span>
    );
  }
  if (st.completa) {
    return (
      <span style={{ ...base, color: '#047857', background: '#d1fae5', border: '1px solid #a7f3d0' }}>
        <CheckCircle2 size={12} /> {st.concluidas}/{st.total} concluídas
      </span>
    );
  }
  return (
    <span style={{ ...base, color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a' }}>
      {st.concluidas}/{st.total} concluídas
    </span>
  );
};

const botaoResposta = (selecionado, cor) => ({
  minWidth: 62, padding: '8px 14px', borderRadius: 8,
  border: `2px solid ${selecionado ? cor : '#e2e8f0'}`,
  background: selecionado ? cor : '#fff',
  color: selecionado ? '#fff' : '#475569',
  fontWeight: 700, cursor: 'pointer', fontSize: 13,
  transition: 'all .12s', fontFamily: 'inherit',
});

export default AutoavaliacaoQuestionario;
